import { useNavigate } from "react-router-dom";
import { SkillBadge } from "./SkillBadge";
import { FileText, Circle, AlertTriangle, Phone, Slash, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useState, useEffect, useRef } from "react";
import { CVFullScreenModal } from "@/components/candidates/CVFullScreenModal";
import { supabase } from "@/integrations/supabase/client";
import { Candidate } from "@/types/candidate";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useCandidateStatus } from "@/utils/candidate/statusUtils";
import { toast } from "@/hooks/use-toast";
import { CLOSED_MATCH_STATUSES_FILTER } from "@/utils/matches/status";

// LocalStorage cache key
const STORAGE_KEY = 'candidate_linkedin_pictures';

// Cache utilities for LinkedIn profile pictures
const linkedInPictureCache = {
	get: (candidateId: string) => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			if (!stored) return null;
			const cache = JSON.parse(stored);
			const entry = cache[candidateId];
			if (!entry) return null;
			// Cache valid for 24 hours
			const maxAge = 24 * 60 * 60 * 1000;
			if (Date.now() - entry.updatedAt > maxAge) return null;
			return entry.pictureUrl;
		} catch {
			return null;
		}
	},

	set: (candidateId: string, pictureUrl: string | null) => {
		try {
			const stored = localStorage.getItem(STORAGE_KEY);
			const cache = stored ? JSON.parse(stored) : {};
			cache[candidateId] = {
				pictureUrl,
				updatedAt: Date.now()
			};
			localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
		} catch {
			// Silent error handling
		}
	}
};

interface CandidateCardProps {
  candidate: Candidate;
  className?: string;
  disableLinks?: boolean;
  isSelected?: boolean;
  isFavorite?: boolean;
}

export const CandidateCard = ({ candidate, className, disableLinks = false, isSelected = false, isFavorite = false }: CandidateCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getStatusColor, getLocalizedStatus } = useCandidateStatus();
  const [cvUrl, setCvUrl] = useState<string | null>(null);
  const [showCVModal, setShowCVModal] = useState(false);
  const skillsContainerRef = useRef<HTMLDivElement>(null);
  const [maxVisibleSkills, setMaxVisibleSkills] = useState<number>(4);
  const [matchCount, setMatchCount] = useState<number>(0);
  const [cvModal, setCvModal] = useState<{ url: string; name: string } | null>(null);
  const [loadingCV, setLoadingCV] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [loadingPicture, setLoadingPicture] = useState(false);

  // Fetch LinkedIn profile picture if candidate has LinkedIn profile
  useEffect(() => {
	if (!candidate.linkedin) return;

	const fetchProfilePicture = async () => {
		// Check cache first
		const cachedPicture = linkedInPictureCache.get(candidate.id);
		if (cachedPicture !== null) {
			setProfilePictureUrl(cachedPicture);
			return;
		}

		setLoadingPicture(true);
		try {
			const { data, error } = await supabase.functions.invoke(
				`unipile-handler?command=get-participant-detail-for-entity&entity_type=candidate&entity_id=${candidate.id}`,
				{ method: 'GET' }
			);

			if (error) {
				linkedInPictureCache.set(candidate.id, null);
				return;
			}

			if (data && !data.error && data.picture_url) {
				setProfilePictureUrl(data.picture_url);
				linkedInPictureCache.set(candidate.id, data.picture_url);
			} else {
				linkedInPictureCache.set(candidate.id, null);
			}
		} catch (err) {
			linkedInPictureCache.set(candidate.id, null);
		} finally {
			setLoadingPicture(false);
		}
	};

	fetchProfilePicture();
  }, [candidate.id, candidate.linkedin]);

  // Fetch the actual match count for this candidate, excluding closed matches
  useEffect(() => {
    const fetchMatchCount = async () => {
      try {
        const { count, error } = await supabase
          .from('matches')
          .select('id', { count: 'exact', head: false })
          .eq('candidate_id', candidate.id)
          .not('status', 'in', CLOSED_MATCH_STATUSES_FILTER);
          
        if (error) {
          console.error("Error fetching match count:", error);
          return;
        }
        
        setMatchCount(count || 0);
      } catch (err) {
        console.error("Failed to fetch match count:", err);
      }
    };
    
    fetchMatchCount();
  }, [candidate.id]);

  useEffect(() => {
    if (!skillsContainerRef.current) return;
    const container = skillsContainerRef.current;
    const badges = Array.from(container.children) as HTMLElement[];
    if (badges.length === 0) return;

    // Reset all badges to display so we can measure
    badges.forEach(badge => badge.style.display = "");

    let totalHeight = 0;
    let lastBottom = 0;
    let visibleCount = 0;
    const maxHeight = 64; // px, adjust as needed (e.g. 2 lines)

    for (let i = 0; i < badges.length; i++) {
      const badge = badges[i];
      const rect = badge.getBoundingClientRect();
      if (i === 0) lastBottom = rect.bottom;
      // If badge is on a new line, increase totalHeight
      if (rect.top > lastBottom) {
        totalHeight += rect.height;
        lastBottom = rect.bottom;
      }
      if (totalHeight + rect.height > maxHeight) {
        break;
      }
      visibleCount++;
    }
    setMaxVisibleSkills(visibleCount);
  }, [candidate.skills]);

  const handleViewCV = async () => {
    setLoadingCV(true);
    try {
      // 1. Get the candidate's default_resume (storage_id)
      const { data: candidateData, error: candidateError } = await supabase
        .from('candidates')
        .select('default_resume')
        .eq('id', candidate.id)
        .single();
      if (candidateError || !candidateData?.default_resume) {
        toast({
          title: "No CV Available",
          description: `${candidate.name} doesn't have a CV uploaded yet.`,
          variant: "destructive"
        });
        return;
      }
      const storageId = candidateData.default_resume;
      // 2. Get the display name (optional)
      const { data: doc, error: docError } = await supabase
        .from('documents')
        .select('display_name, name')
        .eq('storage_id', storageId)
        .single();
      const displayName = doc?.display_name || doc?.name || "CV";
      // 3. Call the edge function using supabase.functions.invoke
      const { data, error } = await supabase.functions.invoke(`document-manager?command=get-document-url&documentStorageId=${storageId}`, {
        method: 'GET',
      });
      
      if (error) throw new Error(`Edge function error: ${error.message}`);
      if (!data?.signedUrl) throw new Error("No signed URL returned");
      
      let url = data.signedUrl;
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        url = url.replace('http://kong:8000', 'http://localhost:54321');
      }
      setCvModal({ url, name: displayName });
    } catch (err) {
      toast({
        title: "Error Loading CV",
        description: "Failed to load CV preview. Please try again.",
        variant: "destructive"
      });
      setCvModal(null);
    } finally {
      setLoadingCV(false);
    }
  };

  const lowercaseStatus = candidate.status?.toLowerCase();

  const candidateUrl = `/candidates/${candidate.id}`;

  // Handle link clicks - only intercept regular clicks for React Router navigation
  // For modifier clicks (ctrl/cmd), let the browser handle it naturally via the href
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // For modifier key clicks, let browser handle native <a> behavior
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0)
      return;
    
    // For regular clicks, use React Router
    e.preventDefault();
    navigate(candidateUrl);
  };

  return (
    <div className={cn(
      "relative flex flex-col h-full overflow-hidden transition-all duration-300 hover:shadow-lg backdrop-blur-lg bg-white/80 dark:bg-black/60 border border-white/30 dark:border-white/20 rounded-sm",
      "hover:bg-white/90 dark:hover:bg-black/70 hover:scale-[1.02] hover:border-white/40 dark:hover:border-white/30",
      isSelected && "ring-2 ring-blue-500 border-blue-500",
      className,
      "w-full h-full"
    )}>
      <div className="p-4 border-b border-border flex flex-row justify-between items-start min-h-[60px]">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Avatar className="h-10 w-10 flex-shrink-0">
            {profilePictureUrl ? (
              <AvatarImage src={profilePictureUrl} alt={candidate.name} />
            ) : null}
            <AvatarFallback className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              {candidate.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </AvatarFallback>
          </Avatar>
          {disableLinks ? (
            <div className={cn(
              "hover:text-primary transition-colors flex-1 min-w-0 flex items-center gap-2",
              "cursor-default"
            )}>
              <h3 className="text-lg font-semibold truncate mb-1 text-gray-900 dark:text-gray-100">{candidate.name}</h3>
              {isFavorite && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
            </div>
          ) : (
            <a
              href={candidateUrl}
              onClick={handleLinkClick}
              className={cn(
                "hover:text-primary transition-colors flex-1 min-w-0 flex items-center gap-2"
              )}
            >
              <h3 className="text-lg font-semibold truncate mb-1 text-gray-900 dark:text-gray-100">{candidate.name}</h3>
              {isFavorite && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
            </a>
          )}
        </div>
        <div 
          className={cn(
            "flex flex-col items-center cursor-pointer ml-2",
            !candidate.default_resume && "cursor-default"
          )}
          onClick={candidate.default_resume ? handleViewCV : undefined}
        >
          {candidate.default_resume ? (
            <>
              <FileText className="h-5 w-5 text-blue-500" />
              {loadingCV && <span className="text-xs text-gray-400 mt-1">{t("common.loading")}</span>}
            </>
          ) : (
            <span className="relative inline-block h-5 w-5">
              <FileText className="h-5 w-5 text-gray-300" />
              <Slash className="absolute inset-0 h-5 w-5 text-gray-400" />
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-between p-4 gap-2 pb-0">
        <div className="flex flex-col gap-1 min-h-[40px]">
          <span className="text-sm text-gray-500 dark:text-gray-400">{candidate.function || <span>&nbsp;</span>}</span>
        </div>
        <div className="flex flex-col gap-1 min-h-[48px]">
          <span className="text-sm text-gray-500 dark:text-gray-400">Email: {candidate.email ? <a href={`mailto:${candidate.email}`} className="text-blue-500 hover:underline dark:text-blue-400">{candidate.email}</a> : <span className="text-gray-400">Not provided</span>}</span>
          <span className="text-sm text-gray-500 dark:text-gray-400">Phone: {candidate.phone ? <a href={`tel:${candidate.phone}`} className="text-blue-500 hover:underline dark:text-blue-400">{candidate.phone}</a> : <span className="text-gray-400">Not provided</span>}</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2 min-h-[24px]">
          {candidate.skills && candidate.skills.length > 0 ? (
            <>
              {candidate.skills.slice(0, 3).map((skill) => (
                <SkillBadge key={skill} label={skill} className="text-xs py-1 px-2 h-6" />
              ))}
              {candidate.skills.length > 3 && (
                <SkillBadge
                  key="more-badge"
                  label={`+${candidate.skills.length - 3} more`}
                  className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 text-xs py-1 px-2 h-6"
                />
              )}
            </>
          ) : (
            <span className="text-gray-400">No skills</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className={cn("text-xs flex items-center gap-1", getStatusColor(candidate.status))}>
            <Circle className="h-3 w-3" />
            {getLocalizedStatus(candidate.status)}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Matches:</span>
          <span className="font-medium text-blue-500 dark:text-blue-400">{matchCount}</span>
        </div>
      </div>
      <div className="flex justify-end items-end p-4 pt-2">
        {disableLinks ? (
          <div className={cn(
            "inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-sm bg-blue-500 text-white hover:bg-blue-500 transition-colors dark:bg-blue-500 dark:hover:bg-blue-500",
            "cursor-default hover:bg-blue-500 dark:hover:bg-blue-500"
          )}>
            {t("contacts.view")}
          </div>
        ) : (
          <a
            href={candidateUrl}
            onClick={handleLinkClick}
            className={cn(
              "inline-flex items-center justify-center px-3 py-1 text-sm font-medium rounded-sm bg-blue-500 text-white hover:bg-blue-500 transition-colors dark:bg-blue-500 dark:hover:bg-blue-500"
            )}
          >
            {t("contacts.view")}
          </a>
        )}
      </div>
      {cvModal && (
        <CVFullScreenModal
          cvUrl={cvModal.url}
          name={cvModal.name}
          onClose={() => setCvModal(null)}
        />
      )}
    </div>
  );
};
